import axios from "axios";
import { api } from "../../utils/utils";
import SettingsLayout from "./SettingsLayout";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { RootState, useAppDispatch } from "../../store";
import { Alert, Button, CircularProgress, Typography } from "@mui/material";
import { Organization, SuperOrganization } from "../../types";
import { updateOrganizationAttribute } from "../../slices/AuthSlice";
import { Colors } from "../../Colors";
import { Link } from "@mui/material";
import { CustomButton, PrimaryButton } from "../../subcomponents/CustomButton";

export default function PaymentProcessing() {
    const dispatch = useAppDispatch();

    const token = useSelector((state: RootState) => state.auth.user?.token);
    const superOrganization: SuperOrganization | null = useSelector((state: RootState) => state.auth.superOrganization);

    const [loading, setLoading] = useState(true);
    const [error, setError] = useState("");

    const [loginLink, setLoginLink] = useState("");

    useEffect(() => {
        if (token && superOrganization) {
            setLoading(false);
        }
    }, [token, superOrganization]);

    async function getLoginLink() {
        try {
            setLoading(true);
            if (token && superOrganization?.stripeConnectedAccountId) {
                const response = await api.post("/stripe-connect/login-link", token, { accountId: superOrganization.stripeConnectedAccountId })
                setLoginLink(response.data.loginLink.url);
            }
        } catch (error) {
            console.log(error);
        } finally {
            setLoading(false);
        }
    }

    useEffect(() => {
        if (token && superOrganization?.stripeConnectedAccountId) {
            getLoginLink();
            
        }
    }, [superOrganization?.stripeConnectedAccountId])


    async function handleCreateAccount() {
        if (!token || !superOrganization || superOrganization.stripeConnectedAccountId) {
            return;
        }
        try {
            setLoading(true);
            const response = await api.post("/stripe-connect/account", token, {});
            const accountId = response.data.account;
            await dispatch(updateOrganizationAttribute({ token: token, attribute: "stripeConnectedAccountId", value: accountId }));
            setError("")
        } catch (error) {
            console.log(error);
            setError("There was an error creating your account. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    async function handleStartOnboarding() {
        if (!token || !superOrganization || !superOrganization.stripeConnectedAccountId) {
            return;
        }
        try {
            setLoading(true);
            const response = await api.post("/stripe-connect/account-link", token, { accountId: superOrganization.stripeConnectedAccountId });
            const accountLink = response.data.accountLink.url;
            setError("")
            window.location.href = accountLink;
        } catch (error) {
            console.log(error);
            setError("There was an error starting the onboarding process. Please try again.");
        } finally {
            setLoading(false);
        }
    }

    let content;
    if (loading) {
        content = (
            <CircularProgress size={30} />
        )
    } else if (superOrganization?.stripeConnectedAccountId && !loginLink) {
        content = (
            <>
                <h5 style={{ marginBottom: "0px", fontSize: "0.9rem" }}>Add information to your Stripe account</h5>
                <p style={{ fontSize: "0.9rem", marginTop: "8px", marginBottom: "8px", maxWidth: "600px", lineHeight: "1.5", color: Colors.grey1 }}>Your Stipe account is now active! Please complete the onboarding process to start accepting payments.</p>
                <PrimaryButton onClick={handleStartOnboarding} style={{ marginTop: "40px" }}>Start onboarding</PrimaryButton>
            </>
        )
    } else if (!superOrganization?.stripeConnectedAccountId) {
        content = (
            <>
                <div>
                    <h5 style={{ marginBottom: "0px", fontSize: "0.9rem" }}>Start accepting payments from patients by creating a Stripe account.</h5>
                    <p style={{ fontSize: "0.9rem", marginTop: "8px", marginBottom: "8px", maxWidth: "600px", lineHeight: "1.5", color: Colors.grey1 }}>Stripe Connect allows Penciled to collect payments from patients on your behalf. The agent will request a payment from the patient, the patient pays through Stripe, and you get paid! It's that easy!</p>
                    <Link href="https://stripe.com/connect" target="_blank" rel="noreferrer">
                        Learn more
                    </Link>
                </div>
                
                <PrimaryButton style={{ marginTop: "40px" }} onClick={handleCreateAccount}>Create account</PrimaryButton>
            </>
        )
    } else if (loginLink) {
        content = (
            <div>
                <h5 style={{ marginBottom: "0px", fontSize: "1rem" }}>Stripe Express Dashboard</h5>
                <p style={{ fontSize: "0.9rem", marginTop: "8px", marginBottom: "8px", maxWidth: "600px", lineHeight: "1.5", color: Colors.grey1 }}>Your Stripe account is successfully integrated! Access your dashboard to track earnings, manage payments, and view transaction history.</p>
                <div style={{ display: "flex", justifyContent: "start", alignItems: "center", gap: "20px", marginTop: "40px" }}>
                    <PrimaryButton onClick={() => window.open(loginLink, "_blank")}>Go to dashboard</PrimaryButton>
                </div>
            </div>
        )
    }
    return (
        <SettingsLayout>
            <Typography variant="h5" sx={{ mb: 3}}>Payment Processing</Typography>
            {error && <Alert severity="error">{error}</Alert>}
            {content}
        </SettingsLayout>
    )
}